import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './../Styles/AssignGoal.css';
import { useToast } from './ToastContext';
import { FiChevronDown } from 'react-icons/fi';
const apiUrl = process.env.REACT_APP_API_URL;

const AssignGoal = ({ onClose, loggedInManagerId, isMainManagerFlag }) => {
    const showToast = useToast();
    const [goals, setGoals] = useState([]);
    const [leads, setLeads] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [managers, setManagers] = useState([]);

    const [selectedGoals, setSelectedGoals] = useState([]);
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedManagers, setSelectedManagers] = useState([]);

    const [goalsDropdownOpen, setGoalsDropdownOpen] = useState(false);
    const [leadsDropdownOpen, setLeadsDropdownOpen] = useState(false);
    const [employeesDropdownOpen, setEmployeesDropdownOpen] = useState(false);
    const [managersDropdownOpen, setManagersDropdownOpen] = useState(false);

    const [showUpdateMessage, setShowUpdateMessage] = useState(false);
    const [alreadyAssignedMessage, setAlreadyAssignedMessage] = useState('');

    const goalsDropdownRef = useRef(null);
    const leadsDropdownRef = useRef(null);
    const employeesDropdownRef = useRef(null);
    const managersDropdownRef = useRef(null);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const goalsResponse = await axios.get(`${apiUrl}/api/goals`);
                setGoals(goalsResponse.data);

                if (isMainManagerFlag) {
                    const managersResponse = await axios.get(`${apiUrl}/api/sub-managers`);
                    setManagers(managersResponse.data || []);
                }

                const leadsResponse = await axios.get(`${apiUrl}/api/leads`, {
                    params: {
                        managerId: loggedInManagerId,
                        isMainManager: isMainManagerFlag
                    }
                });
                setLeads(leadsResponse.data || []);
                
                const employeesResponse = await axios.get(`${apiUrl}/api/employees`, {
                    params: {
                        managerId: loggedInManagerId, 
                        isMainManager: isMainManagerFlag 
                    }
                });
                // Filter out employees who have been upgraded to lead
                const allEmps = employeesResponse.data || [];
                setEmployees(allEmps.filter(e => e.role !== 'lead'));
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [loggedInManagerId, isMainManagerFlag]); 

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (goalsDropdownRef.current && !goalsDropdownRef.current.contains(event.target)) {
                setGoalsDropdownOpen(false);
            }
            if (leadsDropdownRef.current && !leadsDropdownRef.current.contains(event.target)) {
                setLeadsDropdownOpen(false);
            }
            if (employeesDropdownRef.current && !employeesDropdownRef.current.contains(event.target)) {
                setEmployeesDropdownOpen(false);
            }
            if (managersDropdownRef.current && !managersDropdownRef.current.contains(event.target)) {
                setManagersDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleCheckboxChange = (setStateFunction, value) => {
        setStateFunction(prev => {
            if (prev.includes(value)) {
                return prev.filter(item => item !== value);
            } else {
                return [...prev, value];
            }
        });
    };

    const handleAssignGoal = async () => {
        const targetIds = Array.from(new Set([...selectedManagers, ...selectedLeads, ...selectedEmployees]));
        if (selectedGoals.length === 0 || targetIds.length === 0) {
            showToast('Please select at least one goal and at least one manager, lead, or employee.', 'warning');
            return;
        }
        setIsLoading(true);
        setAlreadyAssignedMessage('');
        setShowUpdateMessage(false);
        try {
            await axios.post(`${apiUrl}/api/assign-goal`, {
                goalIds: selectedGoals,
                employeeIds: targetIds
            });
            setShowUpdateMessage(true);
            setSelectedGoals([]);
            setSelectedLeads([]);
            setSelectedEmployees([]);
            setSelectedManagers([]);

            setTimeout(() => {
                setShowUpdateMessage(false);
                onClose();
            }, 2000);
        } catch (error) {
            if (error.response && error.response.status === 409) {
                setAlreadyAssignedMessage(error.response.data.message || 'Goal already assigned to the selected account(s).');
                setSelectedGoals([]);
                setSelectedLeads([]);
                setSelectedEmployees([]);
                setSelectedManagers([]);
                setTimeout(() => setAlreadyAssignedMessage(''), 8000);
            } else {
                console.error('Error assigning goals:', error);
                showToast('Error assigning goals. Please try again.', 'error');
            }
        } finally {
            setIsLoading(false);
        }
    };
   
    return (
        <div className="modal-overlay">
            <div className="modal-content assign-goal-modal-content">
                <h2>Assign Goals</h2>
                
                {showUpdateMessage && (
                    <div className="update-message" style={{ marginTop: '8px', marginBottom: '16px' }}>
                        ✓ Goals assigned successfully!
                    </div>
                )}
                {alreadyAssignedMessage && (
                    <div className="already-assigned-message" style={{ marginTop: '8px', marginBottom: '16px' }}>
                        ⚠️ {alreadyAssignedMessage}
                    </div>
                )}

                {/* 1. Goals Dropdown */}
                <div className="pms-form-group">
                    <label className="pms-label">Select Goals:</label>
                    <div className="assign-dropdown-container" ref={goalsDropdownRef}>
                        <div
                            className="pms-input"
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => {
                                setGoalsDropdownOpen(prev => !prev);
                                setManagersDropdownOpen(false);
                                setLeadsDropdownOpen(false);
                                setEmployeesDropdownOpen(false);
                            }}
                        >
                            <span>{selectedGoals.length === 0 ? 'Select Goals' : `Selected Goals (${selectedGoals.length})`}</span>
                            <FiChevronDown 
                                size={16} 
                                style={{ 
                                    color: 'var(--text-muted)', 
                                    transition: 'transform 0.2s ease', 
                                    transform: goalsDropdownOpen ? 'rotate(180deg)' : 'none' 
                                }} 
                            />
                        </div>
                        {goalsDropdownOpen && (
                            <div className="assign-dropdown-menu">
                                {goals.map(goal => (
                                    <div
                                        key={goal._id}
                                        className="checkbox-item"
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleCheckboxChange(setSelectedGoals, goal._id)}
                                    >
                                        <input
                                            type="checkbox"
                                            id={`goal-${goal._id}`}
                                            checked={selectedGoals.includes(goal._id)}
                                            onChange={() => {}}
                                            style={{ pointerEvents: 'none' }}
                                        />
                                        <label htmlFor={`goal-${goal._id}`} style={{ cursor: 'pointer', pointerEvents: 'none' }}>
                                            {goal.goal_name || goal.goal_description}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Manager Dropdown (Only for Main Manager) */}
                {isMainManagerFlag && (
                    <div className="pms-form-group">
                        <label className="pms-label">Select Manager (Optional):</label>
                        <div className="assign-dropdown-container" ref={managersDropdownRef}>
                            <div
                                className="pms-input"
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => {
                                    setManagersDropdownOpen(prev => !prev);
                                    setGoalsDropdownOpen(false);
                                    setLeadsDropdownOpen(false);
                                    setEmployeesDropdownOpen(false);
                                }}
                            >
                                <span>{selectedManagers.length === 0 ? 'Select Manager (Optional)' : `Selected Manager (${selectedManagers.length})`}</span>
                                <FiChevronDown 
                                    size={16} 
                                    style={{ 
                                        color: 'var(--text-muted)', 
                                        transition: 'transform 0.2s ease', 
                                        transform: managersDropdownOpen ? 'rotate(180deg)' : 'none' 
                                    }} 
                                />
                            </div>
                            {managersDropdownOpen && (
                                <div className="assign-dropdown-menu">
                                    {managers.map(manager => (
                                        <div
                                            key={manager.employee_number}
                                            className="checkbox-item"
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleCheckboxChange(setSelectedManagers, manager.employee_number)}
                                        >
                                            <input
                                                type="checkbox"
                                                id={`manager-${manager.employee_number}`}
                                                checked={selectedManagers.includes(manager.employee_number)}
                                                onChange={() => {}}
                                                style={{ pointerEvents: 'none' }}
                                            />
                                            <label htmlFor={`manager-${manager.employee_number}`} style={{ cursor: 'pointer', pointerEvents: 'none' }}>
                                                {manager.full_name || manager.employee_number}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 2. Lead Dropdown */}
                <div className="pms-form-group">
                    <label className="pms-label">Select Lead (Optional):</label>
                    <div className="assign-dropdown-container" ref={leadsDropdownRef}>
                        <div
                            className="pms-input"
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => {
                                setLeadsDropdownOpen(prev => !prev);
                                setGoalsDropdownOpen(false);
                                setManagersDropdownOpen(false);
                                setEmployeesDropdownOpen(false);
                            }}
                        >
                            <span>{selectedLeads.length === 0 ? 'Select Lead (Optional)' : `Selected Lead (${selectedLeads.length})`}</span>
                            <FiChevronDown 
                                size={16} 
                                style={{ 
                                    color: 'var(--text-muted)', 
                                    transition: 'transform 0.2s ease', 
                                    transform: leadsDropdownOpen ? 'rotate(180deg)' : 'none' 
                                }} 
                            />
                        </div>
                        {leadsDropdownOpen && (
                            <div className="assign-dropdown-menu">
                                {leads.length === 0 ? (
                                    <div className="checkbox-item" style={{ color: 'var(--text-muted)' }}>
                                        No leads found
                                    </div>
                                ) : (
                                    leads.map(lead => (
                                        <div
                                            key={lead.employee_number}
                                            className="checkbox-item"
                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => handleCheckboxChange(setSelectedLeads, lead.employee_number)}
                                        >
                                            <input
                                                type="checkbox"
                                                id={`lead-${lead.employee_number}`}
                                                checked={selectedLeads.includes(lead.employee_number)}
                                                onChange={() => {}}
                                                style={{ pointerEvents: 'none' }}
                                            />
                                            <label htmlFor={`lead-${lead.employee_number}`} style={{ cursor: 'pointer', pointerEvents: 'none' }}>
                                                {lead.full_name || lead.employee_number}
                                            </label>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* 3. Employees Dropdown */}
                <div className="pms-form-group">
                    <label className="pms-label">Select Employees:</label>
                    <div className="assign-dropdown-container" ref={employeesDropdownRef}>
                        <div
                            className="pms-input"
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => {
                                setEmployeesDropdownOpen(prev => !prev);
                                setGoalsDropdownOpen(false);
                                setManagersDropdownOpen(false);
                                setLeadsDropdownOpen(false);
                            }}
                        >
                            <span>{selectedEmployees.length === 0 ? 'Select Employees' : `Selected Employees (${selectedEmployees.length})`}</span>
                            <FiChevronDown 
                                size={16} 
                                style={{ 
                                    color: 'var(--text-muted)', 
                                    transition: 'transform 0.2s ease', 
                                    transform: employeesDropdownOpen ? 'rotate(180deg)' : 'none' 
                                }} 
                            />
                        </div>
                        {employeesDropdownOpen && (
                            <div className="assign-dropdown-menu">
                                {employees.map(employee => (
                                    <div
                                        key={employee.employee_number}
                                        className="checkbox-item"
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleCheckboxChange(setSelectedEmployees, employee.employee_number)}
                                    >
                                        <input
                                            type="checkbox"
                                            id={`employee-${employee.employee_number}`}
                                            checked={selectedEmployees.includes(employee.employee_number)}
                                            onChange={() => {}}
                                            style={{ pointerEvents: 'none' }}
                                        />
                                        <label htmlFor={`employee-${employee.employee_number}`} style={{ cursor: 'pointer', pointerEvents: 'none' }}>
                                            {employee.full_name}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="assigngoal-button-group">
                    <button
                        onClick={handleAssignGoal}
                        disabled={isLoading || showUpdateMessage}
                        className="pms-btn pms-btn-primary"
                    >
                        {isLoading ? (
                            <span>
                                <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                Assigning...
                            </span>
                        ) : (
                            'Assign'
                        )}
                    </button>
                    <button onClick={onClose} className="pms-btn assigngoalclose-button" disabled={isLoading}>Close</button>
                </div>

                {isLoading && (
                    <div className="loading-overlay">
                        <i className="fa fa-spinner fa-spin loading-spinner"></i>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssignGoal;